import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, Result, Button, Typography, Space, Spin } from 'antd';
import { CheckCircleOutlined, HomeOutlined } from '@ant-design/icons';
import { createUseStyles } from 'react-jss';
import { theme } from '../constants/theme';

const { Title, Text, Paragraph } = Typography;

const useStyles = createUseStyles({
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    padding: '40px 20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    maxWidth: '600px',
    width: '100%',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
    '& .ant-card-body': {
      padding: '48px',
    },
  },
  successIcon: {
    fontSize: '72px',
    color: '#10b981',
    marginBottom: '24px',
  },
  title: {
    fontFamily: "'Baloo 2'",
    fontWeight: '600',
    fontSize: '32px !important',
    color: '#1f2937',
    marginBottom: '16px !important',
  },
  subtitle: {
    fontSize: '18px',
    color: '#6b7280',
    marginBottom: '32px !important',
  },
  infoBox: {
    backgroundColor: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '32px',
  },
  buttonGroup: {
    textAlign: 'center',
    '& .ant-btn': {
      marginLeft: '8px',
      marginRight: '8px',
    },
  },
  homeButton: {
    backgroundColor: theme.palette.primary,
    borderColor: theme.palette.primary,
    fontWeight: '600',
    height: '48px',
    paddingLeft: '24px',
    paddingRight: '24px',
    '&:hover': {
      backgroundColor: '#3730a3',
      borderColor: '#3730a3',
    },
  },
  secondaryButton: {
    borderColor: theme.palette.primary,
    color: theme.palette.primary,
    fontWeight: '500',
    height: '48px',
    paddingLeft: '24px',
    paddingRight: '24px',
    '&:hover': {
      borderColor: '#3730a3',
      color: '#3730a3',
    },
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '40px',
  },
});

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const PaymentSuccess: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const query = useQuery();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sessionId = query.get('session_id');

  useEffect(() => {
    // Verify the payment session
    const verifyPayment = async () => {
      if (!sessionId) {
        setError('Missing payment session information');
        setVerifying(false);
        return;
      }

      try {
        // In a real implementation, you might want to verify the payment with your backend
        // For now, we'll just assume it's successful if we have a session ID
        await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulate verification delay
        setVerified(true);
      } catch (error) {
        console.error('Error verifying payment:', error);
        setError('Failed to verify payment');
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [sessionId]);

  const handleGoHome = () => {
    history.push('/');
  };

  const handleContactSupport = () => {
    window.open(
      'mailto:support@sonar.example.com?subject=Payment Success Inquiry',
      '_blank',
    );
  };

  if (verifying) {
    return (
      <div className={classes.container}>
        <Card className={classes.card}>
          <div className={classes.loadingContainer}>
            <Spin size="large" />
            <Title
              level={3}
              style={{ marginTop: '24px', color: theme.palette.primary }}
            >
              Vérification de votre paiement...
            </Title>
            <Text style={{ fontSize: '16px', color: '#6b7280' }}>
              Veuillez patienter pendant que nous confirmons les détails de
              votre paiement.
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !verified) {
    return (
      <div className={classes.container}>
        <Card className={classes.card}>
          <Result
            status="warning"
            title="Vérification du paiement"
            subTitle={
              error ||
              'Nous ne parvenons pas à vérifier votre paiement pour le moment. Si vous avez été débité, votre paiement sera traité et les fonctionnalités premium seront activées sous peu.'
            }
            extra={[
              <Button
                key="home"
                type="primary"
                className={classes.homeButton}
                onClick={handleGoHome}
              >
                Aller à l&apos;accueil
              </Button>,
              <Button
                key="support"
                className={classes.secondaryButton}
                onClick={handleContactSupport}
              >
                Contacter le support
              </Button>,
            ]}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Card className={classes.card}>
        <div style={{ textAlign: 'center' }}>
          <CheckCircleOutlined className={classes.successIcon} />

          <Title level={2} className={classes.title}>
            Paiement réussi !
          </Title>

          <Text className={classes.subtitle}>
            Merci pour votre paiement. L&apos;abonnement premium a été activé.
          </Text>

          <div className={classes.infoBox}>
            <Title level={4} style={{ color: '#1e40af', marginBottom: '16px' }}>
              Que se passe-t-il ensuite ?
            </Title>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>
                ✅ Les fonctionnalités premium ont été automatiquement activées
              </Text>
              <Text>
                ✅ Le propriétaire du projet recevra un e-mail de confirmation
              </Text>
              <Text>✅ Vous recevrez un reçu de paiement par e-mail</Text>
              <Text>✅ L&apos;abonnement commencera immédiatement</Text>
            </Space>
          </div>

          <Paragraph style={{ color: '#6b7280', marginBottom: '32px' }}>
            L&apos;utilisateur Sonar peut maintenant accéder à toutes les
            fonctionnalités premium pour son projet. Si vous avez des questions
            concernant ce paiement, veuillez contacter notre équipe de support.
          </Paragraph>

          <div className={classes.buttonGroup}>
            <Button
              type="primary"
              size="large"
              icon={<HomeOutlined />}
              className={classes.homeButton}
              onClick={handleGoHome}
            >
              Aller à l&apos;accueil Sonar
            </Button>
            <Button
              size="large"
              className={classes.secondaryButton}
              onClick={handleContactSupport}
            >
              Contacter le support
            </Button>
          </div>

          <div
            style={{
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid #e5e7eb',
            }}
          >
            <Text style={{ color: '#9ca3af', fontSize: '14px' }}>
              Paiement traité en toute sécurité par Stripe • ID de transaction :{' '}
              {sessionId?.substring(0, 8)}...
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};
